import { FC } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthorityName } from './roles';
import { savePostLoginRedirect } from './storage';

// Props definitions
interface RouteGuardProps {
  redirectTo: string;
  isAuth: boolean;
  permission?: (_role?: AuthorityName) => boolean;
}

// PrivateRoute
const PrivateRoute: FC<RouteGuardProps> = ({ redirectTo, isAuth, permission }) => {
  const authorityName = useSelector((state: any) => state.user.userInfo?.authorityName);
  const location = useLocation();

  if (!isAuth) {
    // Remember the requested page so login can return the user to it
    savePostLoginRedirect(location.pathname + location.search);
    return <Navigate to={redirectTo} replace />;
  }

  if (permission && !permission(authorityName)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
};

// PublicRoute
const PublicRoute: FC<Omit<RouteGuardProps, 'permission'>> = ({ redirectTo, isAuth }) => {
  if (isAuth) {
    return <Navigate to={redirectTo} replace />;
  }
  return <Outlet />;
};

// RequireNavigationState
// Pages that depend on data passed via navigation state (edit/detail pages) cannot be opened
// directly (new tab, bookmark, shared link). Redirect back to the list instead of rendering
// a broken or misleading page.
interface RequireNavigationStateProps {
  stateKey: string;
  redirectTo: string;
}

const RequireNavigationState: FC<RequireNavigationStateProps> = ({ stateKey, redirectTo }) => {
  const location = useLocation();

  if (!location.state?.[stateKey]) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};

export { PrivateRoute, PublicRoute, RequireNavigationState };
